import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';

/**
 * Data layer for the recruiter "Detalhes" drawer (job-flow) — the generic
 * counterpart to the FatorH sync panel. Backs both entry points:
 *   - the info (ⓘ) icon on any module card → detalhes de UM módulo;
 *   - the "Detalhes" item on a phase header → detalhes de TODA a etapa.
 *
 * Calls the BFF's `/jobs/:id/steps/:stepId/candidates` proxy directly via the
 * SDK `fetchClient` (path not yet in the generated typed SDK — mirrors the
 * fator-h-sync-query approach). Types are declared locally to match Core's
 * shape. Core composes the payload on-demand and NEVER reads the BI-only
 * `job_enrollment`; the display state (Pendente/Em andamento/Concluído) is
 * derived here from the raw progress rows.
 */

export interface StepCandidateModule {
  id: string;
  name: string;
  typeKey: string;
  position: number;
  hidden: boolean;
}

export interface StepCandidateProgress {
  moduleId: string;
  startedAt: string | null;
  completedAt: string | null;
  score: string | null;
}

export interface StepCandidate {
  userId: string;
  kcUserId: string | null;
  name: string | null;
  /** Nome social — o display SEMPRE prefere ele ao civil (EU-594). Opcional
   *  pela janela de deploy do Core. */
  socialName?: string | null;
  email: string | null;
  /** Entrou nesta fase (existe linha em job_step_user_progress). */
  entered: boolean;
  /** Recrutador avançou o candidato adiante (linha da fase = completed). */
  advancedByRecruiter: boolean;
  /** Status cru do ledger nesta fase (in_progress/completed/rejected; null =
   *  nunca entrou). Opcional pela janela de deploy do Core (2026-07-30). */
  stepStatus?: string | null;
  /** Inscrição reprovada (EU-552) — não pode mais ser movimentado. */
  rejected?: boolean;
  progress: StepCandidateProgress[];
  /** Módulos desta fase que o candidato pode acessar mesmo ocultos (whitelist). */
  whitelistedModuleIds: string[];
}

export interface StepCandidatesResponse {
  stepId: string;
  modules: StepCandidateModule[];
  candidates: StepCandidate[];
}

export class StageDetailsError extends Error {
  readonly name = 'StageDetailsError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

export const stageDetailsQueryKey = (jobId: string, stepId: string) =>
  ['recruiter', 'stage-details', jobId, stepId] as const;

/** Candidatos ativos da vaga + progresso por módulo desta etapa — alimenta o drawer. */
export function stageDetailsQueryOptions(jobId: string, stepId: string | null, enabled = true) {
  return queryOptions({
    queryKey: stageDetailsQueryKey(jobId, stepId ?? ''),
    enabled: enabled && jobId.length > 0 && !!stepId,
    queryFn: async (): Promise<StepCandidatesResponse> => {
      const res = await fetchClient<StepCandidatesResponse>({
        method: 'GET',
        url: `/jobs/${jobId}/steps/${stepId}/candidates`,
      });
      if (res.error) {
        throw new StageDetailsError(
          `Falha ao carregar candidatos da etapa (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data;
    },
    // Progress moves as candidates answer; re-fetch on reopen.
    staleTime: 30 * 1000,
  });
}

export type StageProgressStatus = 'completed' | 'in_progress' | 'pending';
export type StagePhaseStatus = StageProgressStatus | 'rejected';

/** Estado de um candidato num MÓDULO específico (drawer aberto pelo ícone do card). */
export function moduleStatusFor(
  candidate: StepCandidate,
  moduleId: string
): { status: StageProgressStatus; score: string | null; completedAt: string | null } {
  const p = candidate.progress.find((x) => x.moduleId === moduleId);
  if (!p) return { status: 'pending', score: null, completedAt: null };
  if (p.completedAt) return { status: 'completed', score: p.score, completedAt: p.completedAt };
  return { status: 'in_progress', score: p.score, completedAt: null };
}

/**
 * Estado de um candidato na ETAPA inteira (drawer aberto pelo header da fase).
 *
 * QA 2026-07-30 — a régua vem do LEDGER (`job_step_user_progress`), não da
 * conclusão de módulos (fase sem módulos ficava "Em andamento" pra sempre,
 * mesmo com o candidato já movido adiante): linha `completed` (avançado pelo
 * recrutador) → Concluído; entrada sem completed → Em andamento; `stepStatus =
 * 'rejected'` (a fase onde a reprovação aconteceu, EU-552) → Reprovado.
 * Progresso em módulo sem entrada (whitelist de módulo oculto) conta como Em
 * andamento; `pending` é fallback defensivo (o endpoint não lista esse caso).
 */
export function phaseStatusFor(
  candidate: StepCandidate,
  /**
   * Ids dos módulos VISÍVEIS da etapa (AA-62 / spec 081 FR-13, 04/09). Fase
   * com módulos conta como Concluída quando o candidato concluiu TODOS eles —
   * é o "Concluiu {fase}" do funil do rh e o conjunto natural de "Passar de
   * fase" (o Core nunca marca o ledger a partir dos módulos; só o avanço do
   * recrutador o faz, então o drawer lia "0 concluído · 3.096 em andamento"
   * com 53 candidatos já terminados). Vazio → régua do ledger, intacta
   * (fase sem módulos — o caso do QA 2026-07-30).
   */
  visibleModuleIds: readonly string[] = []
): StagePhaseStatus {
  if (candidate.stepStatus === 'rejected') return 'rejected';
  if (candidate.advancedByRecruiter) return 'completed';
  if (
    visibleModuleIds.length > 0 &&
    visibleModuleIds.every((id) =>
      candidate.progress.some((p) => p.moduleId === id && p.completedAt)
    )
  ) {
    return 'completed';
  }
  if (candidate.entered || candidate.progress.length > 0) return 'in_progress';
  return 'pending';
}

/**
 * Nota do candidato na ETAPA — o maior score entre os módulos da etapa. Uma
 * etapa pontuada normalmente tem 1 módulo com nota (FatorH / avaliação); etapas
 * de conteúdo (Trilha Online) não têm nota → retorna null (a coluna some).
 */
/**
 * Progresso do candidato nos módulos VISÍVEIS da etapa — n/N concluídos
 * (AA-62 / spec 081 FR-14). Dá nuance ao "Em andamento": 2/3 módulos.
 */
export function candidateModuleProgress(
  candidate: StepCandidate,
  visibleModuleIds: readonly string[]
): { done: number; total: number } {
  const done = visibleModuleIds.filter((id) =>
    candidate.progress.some((p) => p.moduleId === id && p.completedAt)
  ).length;
  return { done, total: visibleModuleIds.length };
}

/**
 * Quantos candidatos da fase concluíram CADA módulo visível, na ordem do fluxo
 * (FR-15 — "igual o RH faz": a conclusão por módulo, não só a da fase toda).
 * Oculto fica fora (whitelist não é funil).
 */
export function moduleCompletionSummary(
  candidates: readonly StepCandidate[],
  modules: readonly StepCandidateModule[]
): { module: StepCandidateModule; done: number }[] {
  return [...modules]
    .filter((m) => !m.hidden)
    .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name))
    .map((module) => ({
      module,
      done: candidates.filter((c) => moduleStatusFor(c, module.id).status === 'completed').length,
    }));
}

export function phaseScoreFor(candidate: StepCandidate): number | null {
  let best: number | null = null;
  for (const p of candidate.progress) {
    if (p.score == null) continue;
    const n = Number(p.score);
    if (!Number.isNaN(n) && (best === null || n > best)) best = n;
  }
  return best;
}

export interface AdvanceResult {
  /** Candidatos que receberam a linha da nova fase. */
  advanced: string[];
  /** Candidatos sem inscrição ativa (silenciosamente ignorados). */
  ignored: string[];
}

/**
 * Passa os `userIds` selecionados PARA `targetStepId` (a próxima fase). Reusa o
 * endpoint formal e consultor-scoped do Core (`POST /jobs/:id/steps/:stepId/advance`,
 * idempotente) via o proxy do BFF — sem endpoint novo.
 */
export async function advanceCandidatesToPhase(
  jobId: string,
  targetStepId: string,
  userIds: string[]
): Promise<AdvanceResult> {
  const res = await fetchClient<AdvanceResult>({
    method: 'POST',
    url: `/jobs/${jobId}/steps/${targetStepId}/advance`,
    data: { userIds },
  });
  if (res.error) {
    throw new StageDetailsError(
      `Falha ao passar candidatos de fase (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data;
}

/** Libera candidatos a acessar um módulo OCULTO (whitelist de teste controlado). */
export async function addModuleWhitelist(
  jobId: string,
  moduleId: string,
  userIds: string[]
): Promise<{ whitelisted: string[] }> {
  const res = await fetchClient<{ whitelisted: string[] }>({
    method: 'POST',
    url: `/jobs/${jobId}/modules/${moduleId}/whitelist`,
    data: { userIds },
  });
  if (res.error) {
    throw new StageDetailsError(
      `Falha ao liberar acesso (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data;
}

/** Remove um candidato da whitelist de um módulo oculto. */
export async function removeModuleWhitelist(
  jobId: string,
  moduleId: string,
  userId: string
): Promise<{ whitelisted: string[] }> {
  const res = await fetchClient<{ whitelisted: string[] }>({
    method: 'DELETE',
    url: `/jobs/${jobId}/modules/${moduleId}/whitelist/${userId}`,
  });
  if (res.error) {
    throw new StageDetailsError(
      `Falha ao remover acesso (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data;
}
